import { Form, Input, Modal, Select, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { AktDatePicker } from "components/aktDatePicker";
import { useFetchMeQuery } from "features/auth/authAPI";
import {
  useUpdateDebtorReminderMutation,
  useCreateDebtorReminderMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { PERMISSIONS } from "features/permissions/constants";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useFetchUserSummariesQuery } from "features/userManagementTable/userManagementAPI";
import moment from "moment-timezone";
import { useParams } from "react-router-dom";
import styled from "styled-components";

const StyledForm = styled(Form)`
  margin-top: 12px;
  max-width: 400px;
`;

function DebtorCreateReminderModal({ open, onOk, onCancel, reminder }) {
  const { debtorId } = useParams();
  const [form] = useForm();
  const { data: me } = useFetchMeQuery();
  const {
    data: { [PERMISSIONS.USER_TASK__ASSIGN_TO_OTHER]: canAssignToOther },
  } = useAuthorizations();

  const { data: users, isLoading: isUsersLoading } = useFetchUserSummariesQuery({
    isActive: true,
  });

  const [updateReminder, { isLoading: isUpdateReminderLoading }] =
    useUpdateDebtorReminderMutation();
  const [createReminder, { isLoading: isCreateReminderLoading }] =
    useCreateDebtorReminderMutation();
  const initialValues = {
    ...reminder,
  };
  const onSubmit = async () => {
    const values = await form.validateFields();

    // If user doesn't have permission to assign to others, force assigneeId to current user
    // Only apply this for NEW reminders, not when updating existing ones
    if (!canAssignToOther && !reminder) {
      values.assigneeId = me?.id;
    }

    const action = reminder ? updateReminder : createReminder;
    const result = await action({
      userTasks: [
        {
          ...values,
          id: reminder?.id,
          debtorId,
        },
      ],
    });
    if ("data" in result) {
      message.success(`Reminder ${reminder ? "updated" : "created"} successfully!`);
      form.resetFields();
      onOk();
    }
  };

  const onModalCancel = () => {
    form.resetFields();
    onCancel();
  };

  const disabledDate = (current) => {
    // Can not select days before today
    return current && current < moment().startOf("day");
  };

  return (
    <Modal
      maskClosable={false}
      title={reminder ? "Update Reminder" : "Create Reminder"}
      open={open}
      onOk={onSubmit}
      onCancel={onModalCancel}
      okText={reminder ? "Update" : "Create"}
      cancelText="Cancel"
      confirmLoading={isUpdateReminderLoading || isCreateReminderLoading}
    >
      <StyledForm
        layout="vertical"
        initialValues={initialValues}
        form={form}
        validateMessages={{ required: "This is a required field" }}
      >
        <Form.Item
          name="remindAt"
          label="Date of Reminder"
          rules={[{ required: true, message: "Please select a reminder date" }]}
        >
          <AktDatePicker showTime placeholder="Select date..." disabledDate={disabledDate} />
        </Form.Item>
        {canAssignToOther && (
          <Form.Item
            name="assigneeId"
            label="Assign To"
            rules={[{ required: true, message: "Please select a user" }]}
            initialValue={me?.id}
          >
            <Select
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              popupMatchSelectWidth={false}
              placeholder="Select a user..."
              loading={isUsersLoading}
              options={users?.map((user) => ({
                label: user.name,
                value: user.id,
              }))}
            />
          </Form.Item>
        )}
        {!canAssignToOther && reminder && (
          <Form.Item
            name="assigneeId"
            label="Assign To"
            rules={[{ required: true, message: "Please select a user" }]}
            initialValue={reminder.assigneeId}
          >
            <Select
              disabled
              showSearch
              filterOption={(input, option) =>
                (option.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
              }
              popupMatchSelectWidth={false}
              placeholder="Select a user..."
              loading={isUsersLoading}
              options={users?.map((user) => ({
                label: user.name,
                value: user.id,
              }))}
            />
          </Form.Item>
        )}
        {!canAssignToOther && !reminder && (
          <Form.Item name="assigneeId" label="Assign To" initialValue={me?.id} hidden>
            <Input type="hidden" />
          </Form.Item>
        )}
        <Form.Item
          name="message"
          label="Reminder"
          rules={[
            { required: true, message: "Please enter reminder text" },
            { min: 1, message: "Reminder text cannot be empty" },
            { max: 500, message: "Reminder text cannot exceed 500 characters" },
          ]}
        >
          <Input.TextArea
            placeholder="Enter reminder details..."
            rows={4}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />
        </Form.Item>
      </StyledForm>
    </Modal>
  );
}

export default DebtorCreateReminderModal;
